import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { memberService } from "../../../services";
import type { Member, MemberStatus } from "../../../services/types";
import { STATUS_BADGE, STATUS_LABEL } from "../../../config/members";
import { totalOutstanding, memberGroupCount, payoutCount } from "../../../utils/member";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { useConfirm } from "../../../context/ConfirmContext";
import { canDeleteMember } from "../../../config/roles";
import { PageHeader, BreadcrumbItem } from "../../../components/feature/PageHeader";
import { Button } from "../../../components/base/Button";
import { Card } from "../../../components/base/Card";
import { Badge } from "../../../components/base/Badge";
import { Avatar } from "../../../components/base/Avatar";
import { StatCard } from "../../../components/base/StatCard";
import { Tabs } from "../../../components/base/Tabs";
import { ErrorState } from "../../../components/base/ErrorState";
import { CurrencyDisplay } from "../../../components/currency/CurrencyDisplay";
import { OverviewTab } from "./components/OverviewTab";
import { GroupsTab } from "./components/GroupsTab";
import { DocumentsTab } from "./components/DocumentsTab";
import { GuarantorsTab } from "./components/GuarantorsTab";
import { PassbookAndLedgerTab } from "./components/PassbookAndLedgerTab";
import { ChangeStatusModal } from "../components/ChangeStatusModal";

export default function MemberProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isViewer, role } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const canDelete = canDeleteMember(role);

  const [member, setMember] = useState<Member | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState("overview");
  const [statusModal, setStatusModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!member) return;
    const ok = await confirm({
      title: "Delete Member",
      message: `Are you sure you want to permanently delete member "${member.fullName}" (${member.memberCode})? This action cannot be undone.`,
      confirmLabel: "Delete Member",
      destructive: true,
      dangerText: "Members with recorded payments or active group enrollments cannot be deleted.",
    });
    if (!ok) return;

    setDeleting(true);
    try {
      await memberService.deleteMember(member.id);
      toast.success("Member deleted", `${member.fullName} was permanently removed.`);
      navigate("/members");
    } catch (err) {
      toast.error("Could not delete member", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    setMember(null);
    try {
      const data = await memberService.getMember(id);
      setMember(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this member.");
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusConfirm = async (status: MemberStatus, reason: string) => {
    if (!member) return;
    try {
      const updated = await memberService.updateMemberStatus(member.id, status, reason, "Staff User");
      setMember(updated);
      setStatusModal(false);
      toast.success("Status updated", `${updated.fullName} is now ${STATUS_LABEL[status].toLowerCase()}.`);
    } catch (err) {
      toast.error("Could not update status", err instanceof Error ? err.message : "Please try again.");
    }
  };

  if (error) {
    return (
      <div>
        <PageHeader title="Member profile" />
        <ErrorState message={error} onRetry={load} />
      </div>
    );
  }

  if (!member) {
    return (
      <div>
        <PageHeader title="Member profile" />
        <div className="space-y-3">
          <div className="h-28 animate-pulse rounded-lg bg-background-200" />
          <div className="h-40 animate-pulse rounded-lg bg-background-200" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Printable branded header (hidden on screen) */}
      <div className="print-only mb-4 border-b-2 border-background-200 pb-3">
        <p className="text-lg font-semibold text-foreground-950">M.K. FINANCIAL SERVICES — Member Profile</p>
        <p className="text-xs text-foreground-500">Printed on {new Date().toLocaleDateString("en-IN")}</p>
      </div>

      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Members" to="/members" />
            <BreadcrumbItem label={member.fullName} isLast />
          </>
        }
        title="Member profile"
        actions={
          <>
            <Button variant="outline" icon={<i className="ri-printer-line" />} onClick={() => window.print()}>
              Print profile
            </Button>
            <Button variant="outline" icon={<i className="ri-add-circle-line" />} disabled={isViewer || true} title="Enrolment arrives in the next phase.">
              Enrol into group
            </Button>
            <Button variant="outline" icon={<i className="ri-pencil-line" />} disabled={isViewer} onClick={() => navigate(`/members/${member.id}/edit`)}>
              Edit
            </Button>
            <Button variant="secondary" icon={<i className="ri-flag-line" />} disabled={isViewer} onClick={() => setStatusModal(true)}>
              Change status
            </Button>
            {canDelete ? (
              <Button
                variant="outline"
                className="border-danger-200 text-danger-600 hover:bg-danger-50 hover:border-danger-300"
                icon={<i className="ri-delete-bin-line" />}
                loading={deleting}
                onClick={handleDelete}
              >
                Delete
              </Button>
            ) : null}
          </>
        }
      />

      {/* Summary panel */}
      <Card className="mb-4" padding={false}>
        <div className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Avatar name={member.fullName} size="lg" />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground-950">{member.fullName}</h2>
                <Badge color={STATUS_BADGE[member.status]} dot>{STATUS_LABEL[member.status]}</Badge>
                {member.isOrganizer ? <Badge color="primary">Organizer</Badge> : null}
              </div>
              <p className="tnum mt-0.5 text-sm text-foreground-500">{member.memberCode}</p>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="inline-flex items-center gap-1"><i className="ri-phone-line text-base text-foreground-400" />{member.primaryPhone}</span>
                {member.city ? (
                  <span className="inline-flex items-center gap-1"><i className="ri-map-pin-line text-base text-foreground-400" />{member.city}</span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 border-t border-background-100 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Groups joined" value={memberGroupCount(member)} icon="ri-archive-line" iconClass="bg-primary-100 text-primary-700" />
          <StatCard
            label="Total outstanding"
            value={<CurrencyDisplay amount={totalOutstanding(member)} tone={totalOutstanding(member) > 0 ? "danger" : "default"} />}
            tone={totalOutstanding(member) > 0 ? "danger" : "default"}
            icon="ri-wallet-3-line"
            iconClass="bg-warning-100 text-warning-700"
          />
          <StatCard label="Payouts taken" value={payoutCount(member)} icon="ri-bank-line" iconClass="bg-success-100 text-success-700" />
          <div
            onClick={() => setTab("passbook")}
            className="cursor-pointer transition-transform hover:scale-[1.01]"
            title="Click to view Passbook & Ledger"
          >
            <StatCard
              label={(member.accountBalance ?? 0) < 0 ? "Advance Deficit (Overdraft)" : "Advance Balance"}
              value={
                <CurrencyDisplay
                  amount={member.accountBalance ?? 0}
                  tone={(member.accountBalance ?? 0) < 0 ? "danger" : (member.accountBalance ?? 0) > 0 ? "success" : "default"}
                />
              }
              tone={(member.accountBalance ?? 0) < 0 ? "danger" : (member.accountBalance ?? 0) > 0 ? "success" : "default"}
              icon="ri-wallet-3-line"
              iconClass={(member.accountBalance ?? 0) < 0 ? "bg-danger-100 text-danger-700" : "bg-emerald-100 text-emerald-700"}
            />
          </div>
        </div>
      </Card>

      <div className="no-print">
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { id: "overview", label: "Overview" },
            { id: "passbook", label: "Passbook & Ledger" },
            { id: "groups", label: "Groups", count: member.enrollments.length },
            { id: "documents", label: "Documents", count: member.documents.length },
            { id: "guarantors", label: "Guarantors", count: member.guarantors.length },
          ]}
        />
      </div>

      <div className="mt-4">
        {tab === "overview" ? <OverviewTab member={member} /> : null}
        {tab === "passbook" ? (
          <PassbookAndLedgerTab member={member} onUpdateMember={setMember} isViewer={isViewer} />
        ) : null}
        {tab === "groups" ? <GroupsTab member={member} /> : null}
        {tab === "documents" ? <DocumentsTab member={member} onUpdate={setMember} isViewer={isViewer} /> : null}
        {tab === "guarantors" ? <GuarantorsTab member={member} onUpdate={setMember} isViewer={isViewer} /> : null}
      </div>

      <ChangeStatusModal
        open={statusModal}
        onClose={() => setStatusModal(false)}
        members={[member]}
        onConfirm={handleStatusConfirm}
        disabled={isViewer}
      />
    </div>
  );
}